/**
 * WelcomeMessage Component
 *
 * Displays time-appropriate greeting and delta insight summary.
 *
 * Atomic level: Molecule
 * Used by: TodayPage
 */

import React from 'react';
import type { WelcomeMessageProps } from '../types/today.types';

/**
 * Welcome message component displaying time-appropriate greeting
 * and a summary of changes in the last 24 hours.
 *
 * @example
 * ```tsx
 * <WelcomeMessage
 *   greeting={{
 *     message: "Good morning, Sarah",
 *     period: "morning",
 *     deltaInsight: "12 new records processed with 2 requiring attention"
 *   }}
 * />
 * ```
 */
export function WelcomeMessage({
  greeting,
}: WelcomeMessageProps): React.ReactElement {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Today
        </p>
        <span className="rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium text-muted-foreground">
          Rolling 24 hours
        </span>
      </div>

      <div className="space-y-2">
        <h1 className="font-jakarta text-3xl font-extrabold tracking-[-0.04em] text-foreground md:text-4xl">
          {greeting.message}
        </h1>
        <p className="max-w-4xl text-[13px] leading-[1.7] text-muted-foreground">
          {greeting.deltaInsight}
        </p>
      </div>
    </div>
  );
}
